import { Texture, Sprite, Graphics, Rectangle, Container, Ticker } from "pixi.js"
import { logger } from "../../../../logger"
import { degrees_to_radians } from "game-common/util"
import { Coordinate, randomIntBetween } from "game-common/models"

// Helper function for random float values
const randomFloatBetween = (min: number, max: number): number => {
    return Math.random() * (max - min) + min
}

export interface FragmentEffectOptions {
    fragmentCount?: number
    duration?: number
    animationSpeed?: number
    effectType?: "explosion" | "crumble" | "dissolve"
    gravity?: number
    bounceFactor?: number
    dustParticles?: number
    groundLevel?: number
}

// An Animator advances one particle by a single frame. It returns true once the
// particle is finished and should be removed.
type Animator = (nowMs: number) => boolean

// All FragmentEffects share ONE Ticker.shared callback rather than each registering their own.
// Cutting several trees at once previously added one ticker callback per effect, each looping its
// own animator array; now a single callback fans out to every active effect once per frame.
const activeEffects: Set<FragmentEffect> = new Set()
let sharedTickerAdded = false

// Hard cap on simultaneous effects. Destroying a whole cluster of trees/walls in one frame used to
// spawn an unbounded burst of sprites+textures and stutter budget machines; past this many active
// effects we simply skip the visual rather than let the allocation storm tank the frame rate.
const MAX_ACTIVE_EFFECTS = 12

const sharedTick = (): void => {
    const now = Date.now()
    activeEffects.forEach(effect => effect.step(now))
}

const ensureSharedTicker = (): void => {
    if (!sharedTickerAdded) {
        Ticker.shared.add(sharedTick)
        sharedTickerAdded = true
    }
}

export class FragmentEffect {
    private pooledItems: Sprite[] = []
    private dustItems: Sprite[] = []
    // All particles for this effect are driven by ONE shared-ticker callback rather
    // than a setInterval per particle. Each tree cut previously started ~16-24
    // independent setInterval(16ms) timers (one per fragment + one per dust particle,
    // plus a nested fade timer per fragment). Those timers fire off the render frame
    // and pile up when several trees are cut in quick succession -- the stutter you see
    // when the splinter animation plays. Ticker.shared is rAF-driven, so every particle
    // now updates exactly once per rendered frame with a single timer for the whole effect.
    private animators: Animator[] = []
    private surface: Graphics
    private parent: Container
    private started: boolean = false
    private released: boolean = false

    constructor(parent: Container) {
        this.parent = parent
        this.surface = new Graphics()
        this.surface.scale.set(1.0)
        this.parent.addChild(this.surface)
    }

    createEffect(texture: Texture, x: number, y: number, options: FragmentEffectOptions = {}): void {
        // Drop the effect entirely if we're already saturated -- see MAX_ACTIVE_EFFECTS.
        if (activeEffects.size >= MAX_ACTIVE_EFFECTS) {
            this.cleanup()
            return
        }
        const {
            fragmentCount = 32,
            duration = 1000,
            effectType = "crumble",
            gravity = 0.3,
            bounceFactor = 0.6,
            dustParticles = 5,
            groundLevel = 0,
        } = options

        this.surface.x = x
        this.surface.y = y

        const { x: origX, y: origY, width, height } = texture._frame

        // Create fragments based on effect type
        for (let i = 0; i < fragmentCount; i++) {
            const fragment = this.createFragment(texture, origX, origY, width, height, i, effectType)
            if (fragment) {
                this.animators.push(
                    this.makeFragmentAnimator(fragment, effectType, duration, gravity, bounceFactor, groundLevel),
                )
            }
        }

        // Add dust particles for crumble effect
        if (effectType === "crumble" && dustParticles > 0) {
            this.createDustParticles(origX, origY, width, height, dustParticles)
        }

        this.start()
    }

    createMultiTextureEffect(
        textures: Array<{ texture: Texture; x: number; y: number }>,
        options: FragmentEffectOptions = {},
    ): void {
        // Drop the effect entirely if we're already saturated -- see MAX_ACTIVE_EFFECTS.
        if (activeEffects.size >= MAX_ACTIVE_EFFECTS) {
            this.cleanup()
            return
        }
        const {
            fragmentCount = 32,
            duration = 1000,
            effectType = "crumble",
            gravity = 0.3,
            bounceFactor = 0.6,
            dustParticles = 8,
            groundLevel = 0,
        } = options

        // Calculate bounds for dust particles
        let minX = Infinity,
            maxX = -Infinity,
            minY = Infinity,
            maxY = -Infinity
        textures.forEach(({ x, y, texture }) => {
            const { width, height } = texture._frame
            if (x < minX) minX = x
            if (x + width > maxX) maxX = x + width
            if (y < minY) minY = y
            if (y + height > maxY) maxY = y + height
        })

        this.surface.x = minX
        this.surface.y = minY

        // Create fragments for each texture
        textures.forEach(({ texture, x, y }) => {
            const { x: origX, y: origY, width, height } = texture._frame
            const localX = x - minX
            const localY = y - minY

            // Create fragments for this texture
            for (let i = 0; i < Math.ceil(fragmentCount / textures.length); i++) {
                const fragment = this.createFragment(texture, origX, origY, width, height, i, effectType)
                if (fragment) {
                    // Adjust fragment position relative to the combined surface
                    fragment.x += localX
                    fragment.y += localY
                    this.animators.push(
                        this.makeFragmentAnimator(
                            fragment,
                            effectType,
                            duration,
                            gravity,
                            bounceFactor,
                            groundLevel - minY,
                        ),
                    )
                }
            }
        })

        // Add dust particles for the entire area
        if (effectType === "crumble" && dustParticles > 0) {
            this.createDustParticles(0, 0, maxX - minX, maxY - minY, dustParticles)
        }

        this.start()
    }

    private createFragment(
        texture: Texture,
        origX: number,
        origY: number,
        width: number,
        height: number,
        index: number,
        effectType: string,
    ): Sprite | null {
        // Fragment size based on effect type - larger chunks for crumble
        let fragWidth: number
        let fragHeight: number

        if (effectType === "crumble") {
            // Larger, more realistic chunks
            fragWidth = randomIntBetween(Math.max(2, Math.floor(width / 4)), Math.max(2, Math.floor(width / 2)))
            fragHeight = randomIntBetween(Math.max(2, Math.floor(height / 4)), Math.max(2, Math.floor(height / 2)))
        } else {
            // Original smaller fragments for explosion
            fragWidth = randomIntBetween(Math.max(1, Math.floor(width / 8)), Math.max(1, Math.floor(width / 3)))
            fragHeight = randomIntBetween(Math.max(1, Math.floor(height / 8)), Math.max(1, Math.floor(height / 3)))
        }

        const fragX = origX + randomIntBetween(0, Math.max(1, width - fragWidth))
        const fragY = origY + randomIntBetween(0, Math.max(1, height - fragHeight))

        const frag = new Texture(
            texture.baseTexture,
            new Rectangle(fragX, fragY, fragWidth, fragHeight),
            undefined,
            undefined,
            texture.rotate,
            texture.defaultAnchor,
        )

        const fragSprite = new Sprite(frag)
        fragSprite.x = fragX - origX
        fragSprite.y = fragY - origY

        this.surface.addChild(fragSprite)
        this.pooledItems.push(fragSprite)

        return fragSprite
    }

    // Build a per-fragment animator. It replaces the old setInterval-based
    // animateFragment: the physics (crumble/explosion), settle detection and the
    // 500ms fade-out are the same, just driven by the shared ticker one frame at a time.
    private makeFragmentAnimator(
        fragSprite: Sprite,
        effectType: string,
        duration: number,
        gravity: number,
        bounceFactor: number,
        groundLevel: number,
    ): Animator {
        const angle = degrees_to_radians(randomIntBetween(0, 360))

        let velocity: Coordinate
        let velocityY = 0
        let rotation = 0
        const startTs = Date.now()
        let changedColor1 = false
        let changedColor2 = false

        // fade-out phase state
        let fading = false
        let fadeOutStart = 0
        const fadeOutDuration = 500
        let fadeStartAlpha = 0

        if (effectType === "crumble") {
            // Crumble physics - more realistic chunk behavior
            const chunkType = randomIntBetween(0, 3)
            let horizontalForce = randomIntBetween(2, 8)
            let verticalForce = randomIntBetween(-2, 2) // Some chunks start falling immediately

            switch (chunkType) {
                case 0: // Heavy chunk - falls fast, bounces little
                    horizontalForce *= 0.7
                    verticalForce -= 1
                    break
                case 1: // Light chunk - floats more, bounces more
                    horizontalForce *= 1.2
                    verticalForce += 2
                    break
                case 2: // Flat chunk - slides more horizontally
                    horizontalForce *= 1.5
                    verticalForce -= 0.5
                    break
            }

            velocity = {
                x: Math.cos(angle) * horizontalForce,
                y: Math.sin(angle) * horizontalForce * 0.3, // Less vertical spread
            }
            velocityY = verticalForce
        } else {
            // Explosion physics (original)
            velocity = {
                x: Math.cos(angle) * 10,
                y: Math.sin(angle) * 10,
            }
        }

        return (now: number): boolean => {
            if (fading) {
                const fadeProgress = (now - fadeOutStart) / fadeOutDuration
                fragSprite.alpha = Math.max(0, fadeStartAlpha - fadeProgress * fadeStartAlpha)
                return fadeProgress >= 1 || fragSprite.alpha <= 0
            }

            if (effectType === "crumble") {
                // Apply gravity
                velocityY += gravity
                fragSprite.y += velocityY
                fragSprite.x += velocity.x * 0.3 // Slower horizontal movement

                // Bounce effect when hitting ground
                if (fragSprite.y >= groundLevel && velocityY > 0) {
                    velocityY *= -bounceFactor
                    fragSprite.y = groundLevel
                    // Add some horizontal friction
                    velocity.x *= 0.8
                }

                // Rotation based on movement and gravity
                fragSprite.angle += (velocityY + Math.abs(velocity.x)) * 0.05

                // Fade out more gradually
                fragSprite.alpha -= 0.008
            } else {
                // Original explosion effect
                fragSprite.x += velocity.x
                fragSprite.y += velocity.y
                rotation += 1
                if (rotation > 360) rotation = 0
                fragSprite.angle = rotation
                fragSprite.alpha -= 0.1

                // Color changes for explosion effect
                if (fragSprite.alpha < 0.7 && !changedColor1) {
                    fragSprite.tint = 0xffc0c0
                    changedColor1 = true
                }
                if (fragSprite.alpha < 0.5 && !changedColor2) {
                    fragSprite.tint = 0xff0000
                    changedColor2 = true
                }
            }

            // Check if fragment should start fading out
            const isSettled =
                effectType === "crumble" &&
                Math.abs(velocityY) < 0.1 &&
                Math.abs(velocity.x) < 0.1 &&
                fragSprite.alpha < 0.8

            // If settled or duration exceeded, start fade out instead of immediate cleanup
            if (isSettled || now - startTs > duration) {
                fading = true
                fadeOutStart = now
                fadeStartAlpha = fragSprite.alpha // Start from current alpha
            }

            return false
        }
    }

    private createDustParticles(origX: number, origY: number, width: number, height: number, count: number): void {
        for (let d = 0; d < count; d++) {
            // Dust is a tinted sprite off the shared Texture.WHITE rather than its own Graphics
            // object. Graphics don't batch (each is its own draw call); WHITE-textured sprites all
            // batch together, so a burst of dust is a handful of draw calls instead of one per mote.
            const dust = new Sprite(Texture.WHITE)
            dust.tint = 0x8b7355 // Brown dust color
            const size = randomIntBetween(2, 4)
            dust.width = size
            dust.height = size

            dust.x = randomIntBetween(0, width)
            dust.y = randomIntBetween(0, height)
            dust.alpha = 0.6
            const jitter = randomFloatBetween(0.5, 1.5)
            dust.scale.set(dust.scale.x * jitter, dust.scale.y * jitter)

            this.surface.addChild(dust)
            this.dustItems.push(dust)

            const dustVelocity = {
                x: randomFloatBetween(-1, 1),
                y: randomFloatBetween(-2, -4), // Upward movement
            }

            this.animators.push((): boolean => {
                dust.x += dustVelocity.x
                dust.y += dustVelocity.y
                dust.alpha -= 0.02
                dust.scale.set(dust.scale.x * 0.98) // Shrink slightly

                if (dust.alpha <= 0) {
                    if (dust.parent) {
                        dust.parent.removeChild(dust)
                    }
                    return true
                }
                return false
            })
        }
    }

    // Advance every particle in this effect by one frame. Called once per frame by the shared
    // ticker (sharedTick), not registered as its own Ticker callback.
    step(now: number): void {
        if (this.released) {
            return
        }
        for (let i = this.animators.length - 1; i >= 0; i--) {
            let done = false
            try {
                done = this.animators[i](now)
            } catch (e) {
                logger.error({ tags: ["fragment", "error"] }, e)
                done = true
            }
            if (done) {
                this.animators.splice(i, 1)
            }
        }

        if (this.animators.length === 0) {
            this.cleanup()
        }
    }

    private start(): void {
        if (this.started) {
            return
        }
        this.started = true
        if (this.animators.length === 0) {
            // Nothing to animate; tear down immediately.
            this.cleanup()
            return
        }
        activeEffects.add(this)
        ensureSharedTicker()
    }

    private cleanup(): void {
        if (this.released) {
            return
        }
        this.released = true

        activeEffects.delete(this)
        this.animators = []

        this.pooledItems.forEach(p => {
            try {
                if (p?.parent) {
                    p.parent.removeChild(p)
                    p.texture.destroy()
                }
            } catch (e) {
                logger.error({ tags: ["fragment", "error"] }, e)
            }
        })

        this.dustItems.forEach(dust => {
            try {
                if (dust?.parent) {
                    dust.parent.removeChild(dust)
                }
                dust.destroy()
            } catch (e) {
                logger.error({ tags: ["fragment", "error"] }, e)
            }
        })

        this.pooledItems = []
        this.dustItems = []

        if (this.surface.parent) {
            this.surface.parent.removeChild(this.surface)
        }
    }

    destroy(): void {
        this.cleanup()
    }
}
